import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import {
  useGetDailySummaryListQuery,
  type DailySummaryListParams,
} from "~/shared/api/daily-summary/daily-summary.api";
import { toDateParam } from "~/shared/utils/filterDateFormatter";

type DailySummaryJson = ConnectProto.DailySummaryJson;

export interface WasteStatus {
  date: string;
  locationPlan: number;
  locationPlanPercent: number;
  locationVisited: number;
  locationVisitedPercent: number;
  locationWaiting: number;
  locationWaitingPercent: number;
}

export interface UseWasteStatusParams {
  districtOrganizationIds: (string | number | bigint)[];
  serviceProviderIds: (string | number | bigint)[];
  // Day the summary is scoped to. Defaults to today when not provided.
  selectedDate?: Date;
}

export const useWasteStatus = (params: UseWasteStatusParams): WasteStatus => {
  // Use the first selected service provider ID, or undefined to get all
  const ownerOrgId =
    params.serviceProviderIds.length > 0 ? params.serviceProviderIds[0].toString() : undefined;

  // Scope the summary to selectedDate (single day) as YYYY-MM-DD, defaulting to today.
  const summaryDate = toDateParam(params.selectedDate ?? new Date());

  const districtOrganizationIds =
    params.districtOrganizationIds.length > 0
      ? params.districtOrganizationIds.map(String)
      : undefined;

  const queryParams: DailySummaryListParams = {
    owner_organization_id: ownerOrgId,
    district_organization_ids: districtOrganizationIds,
    date_from: summaryDate,
    date_to: summaryDate,
  };

  const { data: summaryData } = useGetDailySummaryListQuery(queryParams);

  return useMemo(() => {
    const pct = (val: number, total: number) =>
      total > 0 ? Number(((val / total) * 100).toFixed(1)) : 0;

    // Aggregate across all items (API returns object with numeric keys)
    const items: DailySummaryJson[] = summaryData ? Object.values(summaryData) : [];

    let standsPlan = 0;
    let standsVisited = 0;
    let standsNotVisited = 0;

    for (const item of items) {
      standsPlan += Number(item.stands_plan ?? 0);
      standsVisited += Number(item.stands_visited ?? 0);
      standsNotVisited += Number(item.stands_not_visited ?? 0);
    }

    const locationPlan = standsPlan;
    const locationVisited = standsVisited;
    const locationWaiting = standsNotVisited;

    return {
      date: (params.selectedDate ?? new Date()).toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      locationPlan,
      locationPlanPercent: pct(locationPlan, locationPlan),
      locationVisited,
      locationVisitedPercent: pct(locationVisited, locationPlan),
      locationWaiting,
      locationWaitingPercent: pct(locationWaiting, locationPlan),
    };
  }, [summaryData, params.selectedDate]);
};
